/* eslint-disable react-hooks/exhaustive-deps */
import CheckboxMUI from '@mui/material/Checkbox';
import { Box, FormControlLabel, FormGroup, Grid, styled } from '@mui/material';
import { Button as ButtonCustom } from '../../../../../components/main-button.component';
import InputSelect from '../../../../../components/select-input.component';
import Button from '@mui/material/Button';
import { IOptionsMenu } from '../../../../../interfaces/select.interface';
import { useStepOne } from '../hooks/useStepOne';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import SaveButton from '../../../../../components/save-button.component';

const CheckBox = styled(CheckboxMUI)(() => ({
	color: 'var(--color-highlight-blue)',
	'&.Mui-checked': {
		color: 'var(--color-highlight-blue)',
	},
}));

const TextButton = styled(Button)(() => ({
	textTransform: 'none',
	color: 'var(--color-primary-main)',
}));

// Alto mínimo de la lista en pantallas bajas; por debajo, la página vuelve a desplazarse
const MIN_LIST_HEIGHT = 280;
// Espacio bajo la barra de botones: padding del Paper y margen inferior del layout
const BOTTOM_RESERVE = 72;

const countSaved = (options: IOptionsMenu[]) =>
	options.filter((option) => option.status === true).length;

interface IStepOneStandars {
	functionalOptions: IOptionsMenu[];
}

export const StepOneStandars = ({ functionalOptions }: IStepOneStandars) => {
	const {
		functionalUnit,
		functionalUnitN2,
		activitie,
		activitiesOptions,
		functionalOptionsN2,
		items,
		checkedList,
		matches,
		selectAll,
		activityBase,
		functionalBase,
		functionalParentBase,
		handleSelectAllItems,
		setFunctionalUnit,
		setFunctionalUnitN2,
		setActivitie,
		navigate,
		onSelect,
		setSelectAll,
		handleSave,
		handleOnContinue,
	} = useStepOne();

	// Avance de lo que habilita «Finalizar criterios»: servicios y, si hay, nivel 2
	const progress = [
		{ name: activityBase?.name ?? 'Actividad', options: activitiesOptions },
		{ name: functionalBase?.name ?? 'Nivel 2', options: functionalOptionsN2 },
	]
		.filter(({ options }) => options.length > 0)
		.map(
			({ name, options }) => `${name} ${countSaved(options)} de ${options.length}`
		);

	const listRef = useRef<HTMLDivElement>(null);
	const footerRef = useRef<HTMLDivElement>(null);
	const [listMaxHeight, setListMaxHeight] = useState<number>(MIN_LIST_HEIGHT);

	useEffect(() => {
		setFunctionalUnit(functionalOptions[0]?.value.toString() || '');
	}, [functionalOptions]);

	// La lista ocupa el espacio que queda en la ventana, para que selectores y botones
	// queden siempre visibles y solo la lista se desplace
	useLayoutEffect(() => {
		const list = listRef.current;
		if (!list) return;
		const scrollContainer = list.closest('main');

		const updateHeight = () => {
			const top =
				list.getBoundingClientRect().top + (scrollContainer?.scrollTop ?? 0);
			const footerHeight = footerRef.current?.offsetHeight ?? 0;
			const available = window.innerHeight - top - footerHeight - BOTTOM_RESERVE;
			setListMaxHeight(Math.max(MIN_LIST_HEIGHT, Math.floor(available)));
		};

		updateHeight();
		const observer = new ResizeObserver(updateHeight);
		const content = scrollContainer?.firstElementChild;
		if (content) observer.observe(content);
		window.addEventListener('resize', updateHeight);
		return () => {
			observer.disconnect();
			window.removeEventListener('resize', updateHeight);
		};
	}, []);

	// Cada servicio nuevo arranca desde el inicio de su lista
	useEffect(() => {
		listRef.current?.scrollTo({ top: 0 });
	}, [items]);

	return (
		<Grid
			container
			marginTop={2}
			spacing={2}
			style={{
				padding: '8px 0px 0px 8px',
			}}
		>
			<Grid item xs={12} sm={6}>
				<span style={{ fontWeight: 'bold' }}>
					Elige {functionalParentBase?.name ?? 'Nivel 1'}:
				</span>
				<InputSelect
					optionsMenu={functionalOptions}
					value={functionalUnit}
					placeholder={`Selecciona ${functionalParentBase?.name ?? 'Nivel 1'}`}
					onChange={() => null}
					isDisabled
				/>
			</Grid>
			{functionalOptionsN2.length > 0 && (
				<Grid item xs={12} sm={6}>
					<span style={{ fontWeight: 'bold' }}>
						Elige {functionalBase?.name ?? 'Nivel 2'}
					</span>
					<InputSelect
						optionsMenu={functionalOptionsN2}
						value={functionalUnitN2}
						placeholder={`Selecciona ${functionalBase?.name ?? 'Nivel 2'}`}
						onChange={(e) => {
							setFunctionalUnitN2(e.target.value?.toString() || '');
						}}
						withStatus
						id="select-lvl2"
					/>
				</Grid>
			)}
			<Grid item xs={12} sm={6}>
				<span style={{ fontWeight: 'bold' }}>
					{activityBase?.name ?? 'Actividad'}:
				</span>
				<InputSelect
					optionsMenu={activitiesOptions}
					value={activitie}
					placeholder={`Selecciona ${activityBase?.name ?? 'servicio'}`}
					onChange={(e) => {
						setActivitie(e.target.value?.toString() || '');
					}}
					withStatus
					id="select-activity"
				/>
			</Grid>
			<Grid item xs={12}>
				<Box
					ref={listRef}
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						justifyContent: 'space-between',
						gap: '40px',
						maxHeight: listMaxHeight,
						overflowY: 'auto',
						border:
							items.length > 0
								? '1px solid var(--color-grey-main)'
								: 'none',
						borderRadius: '8px',
						padding: '0 16px 16px',
					}}
				>
					{items.map((item, column) => (
						<Grid container key={`formGroup-${column}`}>
							<Grid
								item
								xs={12}
								sx={{
									display: 'flex',
									alignItems: 'center',
									justifyContent: 'space-between',
									gap: 2,
									position: 'sticky',
									top: 0,
									zIndex: 1,
									backgroundColor: 'white',
								}}
							>
								<p style={{ fontWeight: 'bold' }}>{item.servicio}</p>
								<TextButton
									sx={{ paddingLeft: 0, fontSize: '12px' }}
									onClick={() => {
										setSelectAll(!selectAll);
										handleSelectAllItems(!selectAll);
									}}
								>
									{selectAll
										? 'Deseleccionar todo'
										: 'Seleccionar todo'}
								</TextButton>
							</Grid>
							<Grid item xs={10}>
								<FormGroup
									sx={{
										display: 'flex',
										width: '100%',
										flexWrap: 'wrap',
										flexDirection: 'row',
									}}
								>
									{item.criterios.map((prop, row) => (
										<div
											key={`prop-${column}-${row}`}
											style={{
												display: 'flex',
												flexDirection: 'column',
												width: 'fit-content',
												gap: '10px',
											}}
										>
											{prop.map((i, idx) => (
												<FormControlLabel
													sx={{ fontSize: '20px' }}
													key={`prop-item-${column}-${row}-${idx}`}
													checked={checkedList.has(i.id)}
													value={i.id}
													control={<CheckBox />}
													label={
														<p
															style={{
																fontSize: '20px',
																margin: 0,
															}}
														>
															{i.name}
														</p>
													}
													onChange={(_, checked) =>
														onSelect(i.id, checked)
													}
													id={`checkbox-${i.id}`}
												/>
											))}
										</div>
									))}
								</FormGroup>
							</Grid>
						</Grid>
					))}
				</Box>
			</Grid>
			<Grid
				item
				xs={12}
				ref={footerRef}
				sx={{
					position: 'sticky',
					bottom: 0,
					zIndex: 2,
					backgroundColor: 'white',
					paddingBottom: 1,
				}}
			>
				<Grid
					container
					alignItems="center"
					justifyContent={matches ? 'center' : 'space-between'}
					spacing={2}
				>
					<Grid item>
						<div style={{ fontWeight: 'bold' }}>
							{checkedList.size}{' '}
							{checkedList.size === 1
								? 'criterio seleccionado'
								: 'criterios seleccionados'}
						</div>
						{progress.length > 0 && (
							<div
								style={{
									fontSize: '14px',
									color: 'var(--color-grey-text)',
								}}
							>
								Guardados: {progress.join(' · ')}
							</div>
						)}
					</Grid>
					<Grid item>
						<Grid container spacing={2} justifyContent="center">
							<Grid item>
								<SaveButton
									id="uf-guardar-btn"
									disabled={checkedList.size === 0}
									onClick={() => handleSave()}
									sx={{
										height: '100%',
										width: '150px',
									}}
								/>
							</Grid>
							<Grid item>
								<ButtonCustom
									label="Cancelar"
									onClick={() => navigate(-1)}
									style={{
										backgroundColor: 'var(--color-error-main)',
										width: '150px',
									}}
								/>
							</Grid>
							<Grid item>
								<ButtonCustom
									label="Finalizar criterios"
									onClick={() => {
										//dispatch(setState({ state: 4 }));
										handleOnContinue();
									}}
									style={{
										backgroundColor: 'var(--color-highlight-blue)',
										width: '150px',
									}}
									disabled={
										functionalOptionsN2.length === 0
											? !activitiesOptions.every(
													(e) => e.status === true
											  )
											: !functionalOptionsN2.every(
													(e) => e.status === true
											  )
									}
									id="finish-indicators"
								/>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</Grid>
	);
};
